import {
    HttpErrorResponse, HttpEvent,
    HttpHandler,
    HttpHeaderResponse,
    HttpInterceptor,
    HttpProgressEvent,
    HttpRequest,
    HttpResponse,
    HttpSentEvent,
    HttpUserEvent
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs/index';
import { filter, map, retryWhen, take, tap, timeout } from 'rxjs/operators';
import { AuthenticationActions } from '../auth/state/authentication.actions';
import { AuthState } from '../auth/state/authentication.reducer';
import { AuthenticationSelectors } from '../auth/state/authentication.selectors';

@Injectable()
export class BackendAPIHttpInterceptor implements HttpInterceptor, OnDestroy {

    public cachedRetrySources: Array<Subject<any>> = [];
    private config: any;
    viewSessionId: string = undefined;
    retryRequests$: Observable<boolean>;
    portalVersion: string = undefined;
    authenticate$: Observable<AuthState>;
    subscription = new Subscription();
    isUserAuthenticated = false;

    constructor(private authenticationSelectors: AuthenticationSelectors,
                private modalService: BsModalService,
                private store: Store<any>,
                private router: Router
    ) {
        this.authenticate$ = this.authenticationSelectors.selectAuthenticate();
        this.retryRequests$ = this.authenticationSelectors.selectRetryRequests();

        const subscription = this.authenticate$
            .subscribe(authenticate => {
                this.viewSessionId = authenticate.viewSessionId;
                this.portalVersion = authenticate.portalVersion;
                this.isUserAuthenticated = authenticate.isUserAuthenticated;
               // Clear cache once session is restored
                if (authenticate.viewSessionId !== undefined) {
                    this.cachedRetrySources = [];
                }
            });

        this.subscription.add(subscription);

    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpSentEvent
        | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any> | HttpErrorResponse | any> {

        let modifiedReq = request.clone();
        let addVersion = false;
        let shouldWait = 60000;

        // Most html, js and css files are packed in bundle files that by Webpack for production are given a version number
        // But for other files - we still add a version number
        if (this.portalVersion !== undefined) {
            // Add a unique ID to the url so it may be cached by the browser
            // but when releasing a new version each of these files will always
            // be loaded rather than using the old version from cache

            // Some html filenames are in fact templates embedded in other files, so adding to the URL will fail

            addVersion = (request.url.indexOf('.html') !== -1 && request.url.indexOf('servlets/') === -1) ||
                    request.url.indexOf('.js') !== -1 || request.url.indexOf('.json') !== -1;
        }

        // The login check is required so that after we logout and login again
        // we can get a new session id
        if (this.viewSessionId !== undefined && this.viewSessionId !== 'undefined' && request.url.indexOf('login') === -1) {
            modifiedReq = request.clone({
                url: addVersion ? `${request.url}?v=${this.portalVersion}` : `${request.url}`,
                headers: request.headers.set('View-Session-Id', `${this.viewSessionId}`)
            });
        }

        if (request.url.indexOf('/targetstats/') !== -1) {
            shouldWait = 300000;
        }


        return next.handle(modifiedReq)
            .pipe(
                retryWhen(errors => {
                    const retrySource = new Subject<any>();
                    errors.subscribe(error => {
                        if (error.status  === 401) {
                            this.router.navigate(['login']);
                        }
                        if (error.status === 440) {
                            if (this.cachedRetrySources.length === 0) {
                                this.cachedRetrySources.push(retrySource);
                                if (this.isUserAuthenticated) {
                                    this.store
                                        .dispatch(AuthenticationActions.sessionTimeout());
                                }
                            }
                        } else {
                            // TODO: Handle other errors that should not complete (e.g. 403)
                            this.config = request.body || {};
                            if (this.config.disableErrorDialog !== undefined && !this.config.disableErrorDialog) {
                                if (this.config.retryBtn !== undefined && this.config.retryBtn) {
                                    if (this.cachedRetrySources.length === 0) {
                                        this.store.dispatch(AuthenticationActions.failedRequest({message: this.config.errorMessage}));
                                        const retrySubscription = this.retryRequests$
                                            .subscribe(retryRequests => {
                                                if (retryRequests) {
                                                    this.store.dispatch(AuthenticationActions.resetFailedRequest());
                                                    this.retryFailedRequests(next);
                                                } else {
                                                    // When user opts for not to retry, clear the cache and return the error
                                                    this.cachedRetrySources = [];

                                                    return retrySource.error(error);
                                                }
                                            });
                                        this.subscription.add(retrySubscription);

                                    }
                                    this.cachedRetrySources.push(retrySource);
                                }
                            } else {
                                retrySource.error(error);
                            }
                        }
                    });

                    return retrySource;
                }),
              // cancel request if more than a minute
              timeout(shouldWait)
            )
          ;
    }

    private retryFailedRequests(next: HttpHandler): void {
        while (this.cachedRetrySources.length) {
            this.cachedRetrySources.shift()   // Take first item
                .next();                      // trigger retry
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
